
import React from 'react';
import { motion } from 'framer-motion';
import WireframeObject from './WireframeObject';
import { MapPin } from 'lucide-react';

interface HeroProps {
  setView: (view: 'home' | 'projects') => void;
}

const Hero: React.FC<HeroProps> = ({ setView }) => {
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById('contact-form');
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="home" className="min-h-screen relative flex flex-col lg:flex-row items-center justify-center px-6 lg:px-20 overflow-hidden pt-24 pb-12 md:pt-20">
      <header className="hidden">
        <h1>Juanma Tena Luque - Creador de Páginas Web en Sevilla | Diseñador UI/UX Senior</h1>
      </header>
      
      {/* Decoración de fondo */}
      <div className="absolute top-[-5%] left-[-5%] w-[60%] h-[40%] bg-[#39FF14]/5 blur-[80px] md:blur-[120px] rounded-full pointer-events-none" />
      
      <div className="flex-1 z-10 text-center lg:text-left w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center justify-center lg:justify-start gap-3 mb-4 md:mb-6">
            <span className="w-8 md:w-12 h-[1px] bg-[#39FF14]/40 hidden xs:block" />
            <span className="text-[10px] md:text-xs font-bold tracking-[0.3em] md:tracking-[0.4em] uppercase text-[#39FF14] neon-glow">
              Creador de Páginas Web en Sevilla
            </span>
          </div>
          
          <h2 className="font-syncopate text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-bold leading-[1.1] mb-6 md:mb-8">
            DISEÑO <br className="hidden sm:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#39FF14] to-emerald-400 neon-glow">
              Y CÓDIGO
            </span> <br className="hidden sm:block" />
            DE ÉLITE
          </h2>
          
          <div className="mb-8 md:mb-12 border-l-0 md:border-l-2 border-[#39FF14]/20 md:pl-6 mx-auto lg:mx-0 max-w-xl text-center lg:text-left">
            <p className="text-gray-400 text-sm md:text-lg lg:text-xl mb-4 leading-relaxed">
              Especialista en transformar visiones complejas en aplicaciones web escalables con React, Next.js y Node.js. Como creador de páginas web, posiciono tu marca en el futuro digital de Sevilla.
            </p>
            <div className="space-y-1">
              <div className="flex items-center justify-center lg:justify-start gap-2 text-[10px] md:text-xs font-bold text-[#39FF14] uppercase tracking-widest">
                <MapPin size={14} className="text-[#39FF14]" />
                Ubicado en Sevilla | Proyectos locales y remotos
              </div>
              <div className="text-[9px] md:text-[10px] text-gray-500 font-bold uppercase tracking-[0.2em] lg:pl-6">
                Servicios de desarrollo web en Sevilla, Dos Hermanas, Aljarafe y toda Andalucía
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center lg:justify-start">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setView('projects')}
              className="px-8 py-3 md:px-10 md:py-4 bg-[#39FF14] text-black text-xs md:text-base font-bold uppercase tracking-widest rounded-sm shadow-[0_0_20px_rgba(57,255,20,0.4)] transition-all cursor-pointer"
            >
              Ver Proyectos SEO
            </motion.button>
            <motion.button
              whileHover={{ backgroundColor: 'rgba(57, 255, 20, 0.1)' }}
              onClick={scrollToContact}
              className="px-8 py-3 md:px-10 md:py-4 border border-[#39FF14]/40 text-[#39FF14] text-xs md:text-base font-bold uppercase tracking-widest rounded-sm transition-colors text-center cursor-pointer"
            >
              Consultoría Gratis
            </motion.button>
          </div>
        </motion.div>
      </div>

      <div className="flex-1 flex justify-center lg:justify-end mt-12 lg:mt-0 relative w-full overflow-visible">
        <div className="scale-75 sm:scale-90 md:scale-100 transition-transform duration-500">
          <WireframeObject />
        </div>
      </div>
    </section>
  );
};

export default Hero;
